import { Avatar, IconButton, styled, Tooltip } from '@mui/material'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { ReferencedEntityWithIcon } from '../../../../../../domain/value-object/ReferencedEntity'
import { ValuePanelInputRoot } from './commons'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { intl } from '../../../../../../i18n'
import { useProjectMemberRepository } from '../../../../../../services/adaptors/projectMemberRepositoryAdaptor'
import { ProjectMemberSearchOption } from '../../../../../../domain/value-object/ProjectMemberSearchOption'
import { colorPalette } from '../../../../../style/colorPalette'
import ClearIcon from '../../../../../components/icons/ClearIcon'
import {
  ProjectMemberPopper,
  ProjectMemberPopperHandle,
} from '../../../../../features/ProjectMemberPopper'
import { ProjectMemberInput as ProjectMemberSearchInput } from '../../../../../features/ProjectMemberInput'
import { useDebounce } from '../../../../../hooks/useDebounce'

type ProjectMemberInputProps = {
  label: string
  value: ReferencedEntityWithIcon | undefined
  onChange: (v: ReferencedEntityWithIcon | undefined) => void
}

export const ProjectMemberInput = ({
  label,
  value,
  onChange,
}: ProjectMemberInputProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const [searchOptions, setSearchOptions] = useState<
    ProjectMemberSearchOption[]
  >([])
  const debouncedSearchText = useDebounce(searchText, 300)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const popperRef = useRef<ProjectMemberPopperHandle>(null)

  const { searchAsProjectMemberSearchOption } = useProjectMemberRepository()

  // 開いたらフォーカスとオプション取得
  useEffect(() => {
    if (!isOpen) return
    inputRef.current?.focus()
    let isMounted = true
    const fn = async () => {
      const response =
        await searchAsProjectMemberSearchOption(debouncedSearchText)
      if (isMounted) {
        setSearchOptions(response)
      }
    }
    fn()
    return () => {
      isMounted = false
    }
  }, [searchAsProjectMemberSearchOption, debouncedSearchText, isOpen])

  const handleOpen = useCallback(() => {
    setIsOpen(true)
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
    setSearchText('')
  }, [])

  const handleSelect = useCallback(
    (member: ProjectMemberSearchOption) => {
      onChange({
        uuid: member.uuid,
        name: member.name,
        iconUrl: member.iconUrl ?? undefined,
      })
      handleClose()
    },
    [onChange, handleClose]
  )

  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value)
  }, [])

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        event.stopPropagation()
        popperRef.current?.arrowDown()
      } else if (event.key === 'ArrowUp') {
        event.preventDefault()
        event.stopPropagation()
        popperRef.current?.arrowUp()
      } else if (event.key === 'Enter') {
        event.preventDefault()
        popperRef.current?.enter()
      } else if (event.key === 'Escape') {
        handleClose()
      }
    },
    [handleClose]
  )

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      onChange(undefined)
    },
    [onChange]
  )

  const tooltipTitle = useMemo(() => {
    if (isOpen) return ''
    if (!value) return label
    return intl.formatMessage(
      { id: 'singleSheet.header.projectMemberTooltip' },
      { label, name: value.name }
    )
  }, [isOpen, label, value])

  return (
    <Tooltip title={tooltipTitle}>
      <Root ref={containerRef} onClick={!isOpen ? handleOpen : undefined}>
        <SelectableArea>
          <SelectedValueArea>
            {value ? (
              <StyledAvatar src={value.iconUrl} />
            ) : (
              <UnselectedLabel>未設定</UnselectedLabel>
            )}
          </SelectedValueArea>
          {value && (
            <ClearIconButton onClick={handleClear}>
              <ClearIcon />
            </ClearIconButton>
          )}
          <ExpandMoreIcon open={isOpen} />
        </SelectableArea>
        {isOpen && (
          <>
            <FloatingInputArea>
              <ProjectMemberSearchInput
                inputRef={inputRef}
                value={searchText}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                autoFocus={true}
              />
            </FloatingInputArea>
            <ProjectMemberPopper
              ref={popperRef}
              open={isOpen}
              anchorEl={containerRef.current}
              options={searchOptions}
              onSelect={handleSelect}
              inputRef={inputRef}
              searchText={searchText}
              offsetY={40}
            />
            <Backdrop onClick={handleClose} />
          </>
        )}
      </Root>
    </Tooltip>
  )
}

const Root = styled(ValuePanelInputRoot)({
  width: '60px',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
})

const SelectableArea = styled('div')({
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
})

const SelectedValueArea = styled('div')({})

const ExpandMoreIcon = styled(ExpandMoreRoundedIcon)(
  ({ open }: { open: boolean }) => ({
    color: colorPalette.monotone[4],
    transform: open ? 'rotate(180deg)' : 'none',
    transition: 'transform .2s',
  })
)

const ClearIconButton = styled(IconButton)({
  padding: '2px',
})

const FloatingInputArea = styled('div')({
  position: 'absolute',
  top: '100%',
  left: 0,
  display: 'flex',
  alignItems: 'center',
  minWidth: '220px',
  minHeight: '32px',
  backgroundColor: colorPalette.monotone[0],
  boxShadow: `0px 2px 8px 0px ${colorPalette.monotone[4]}80`,
  border: `1px solid ${colorPalette.monotone[2]}`,
  borderRadius: '4px',
  padding: '4px 8px',
  zIndex: 1300,
})

const Backdrop = styled('div')({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1299,
})

const StyledAvatar = styled(Avatar)({
  height: '20px',
  width: '20px',
})

const UnselectedLabel = styled('span')({
  color: colorPalette.monotone[10],
  fontSize: '12px',
})
